import React, { useMemo } from "react";
import { useAtomValue } from "jotai";
import { measureSegmentationResultAtom } from "../../atoms/measureAtoms";
import { SegmentationViewer3D, hullAxesAtom, type HullAxis } from "./SegmentationViewer3D";
import {
  planeContourDataToWorldPolylines,
  type WorldPolyline,
} from "../../utils/contourToWorldPoints";

/**
 * 3D viewer panel for the reporter's click-to-measure flow. Mirrors
 * SegmentationViewerPanel but pulls from measureSegmentationResultAtom
 * (axial-only) and shows API-reported hull stats in the top-right rather
 * than client-computed PCA values.
 */
export const MeasureSegmentationViewerPanel: React.FC = () => {
  const result = useAtomValue(measureSegmentationResultAtom);

  const polylines = useMemo<WorldPolyline[]>(() => {
    if (!result || Object.keys(result.contours).length === 0) return [];
    return planeContourDataToWorldPolylines(
      {
        contours: result.contours,
        spacing: result.spacing,
        dimensions: result.dimensions,
        origin: result.origin,
        rowCosines: result.rowCosines,
        columnCosines: result.columnCosines,
        sliceDirection: result.sliceDirection,
      },
      "axial",
    );
  }, [result]);

  if (!result || polylines.length === 0) {
    return (
      <div className="w-full h-full bg-bg-2 flex items-center justify-center text-fg-muted text-sm">
        Click a finding to measure
      </div>
    );
  }

  return (
    <div className="w-full h-full relative bg-bg-2">
      <SegmentationViewer3D
        polylinesOverride={polylines}
        fillContours
        wrapStyle="loft"
        anatomicalAxes={{
          rowCos: result.rowCosines,
          colCos: result.columnCosines,
          sliceDir: result.sliceDirection,
        }}
      />
      <StatsOverlay volumeMm3={result.volumeMm3} />
    </div>
  );
};

const StatsOverlay: React.FC<{
  volumeMm3: number;
}> = ({ volumeMm3 }) => {
  // Read the same axes the 3D scene draws — when anatomicalAxes is passed
  // these are projection extents along DICOM rowCos/colCos/sliceDir, so
  // each displayed length corresponds to a line you can see in the viewer.
  const hullAxes = useAtomValue(hullAxesAtom);
  return (
    <div className="absolute inset-0 pointer-events-none select-none z-10">
      <div className="absolute" style={{ top: 8, right: 8 }}>
        <div
          className="text-[11px] font-mono leading-relaxed"
          style={{
            color: "#93b4ff",
            textShadow: "0 1px 3px rgba(0,0,0,0.9), 0 0 6px rgba(0,0,0,0.6)",
            border: "1px solid rgba(67, 97, 238, 0.4)",
            borderRadius: 4,
            padding: "4px 6px",
            background: "rgba(0, 0, 0, 0.5)",
          }}
        >
          <div>
            Vol:{" "}
            {volumeMm3 >= 1000
              ? `${(volumeMm3 / 1000).toFixed(1)} cm³`
              : `${volumeMm3.toFixed(0)} mm³`}
          </div>
          {hullAxes?.map((axis: HullAxis, i: number) => (
            <div key={i} style={{ color: axis.color }}>
              <span style={{ fontWeight: 700 }}>{axis.length.toFixed(1)}</span> mm
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
